import { add, greaterThanOrEqual, multiply } from "dinero.js"

import { toCurrency, toDinero, zeroDinero } from "../../../../../../modules/currency/currencyLib"
import { PaymentMethodType } from "../../../../../../modules/payments/paymentTypes"

import type { TakeawayCartItem, TakeawayPaymentMethod } from "../takeawayTypes"

export type CheckoutFormValues = Readonly<{
    selectedMethodValue: string
    orderComment: string
    accountingValues: Record<string, string>
    guestName: string
    guestEmail: string
    guestPhone: string
    termsAccepted: boolean
}>

// Returns true if the given payment method can currently be selected.
export function isValidPaymentMethod(
    method: TakeawayPaymentMethod,
    walletTotal: ReturnType<typeof toDinero> | undefined,
    walletBalance: ReturnType<typeof toDinero> | undefined,
): boolean {
    if (method.card) {
        return !method.card.hasExpired
    }
    if (method.value === PaymentMethodType.GOPAY_WALLET) {
        if (!walletTotal || !walletBalance) return false
        return greaterThanOrEqual(walletBalance, walletTotal)
    }
    return true
}

// API may return the literal string "null" instead of undefined for missing fields.
export function isValidApiString(value: string | undefined): value is string {
    return !!value && value !== "null"
}

// Sum of items in the basket date card — returns null when empty (no currency to infer).
export function calcItemsTotal(items: TakeawayCartItem[]) {
    const priced = items.filter((item) => item.product.price != null)
    if (!priced.length) return null
    return priced
        .map((item) => multiply(toDinero(item.product.price!), item.quantity))
        .reduce((acc, cur) => add(acc, cur))
}

function sumItems(items: TakeawayCartItem[], predicate: (item: TakeawayCartItem) => boolean) {
    const currencyCode = items.find((i) => i.product.price)?.product.price?.currency ?? "DKK"
    const currency = toCurrency(currencyCode)
    return items
        .filter((i) => i.product.price != null && predicate(i))
        .map((i) => multiply(toDinero(i.product.price!), i.quantity))
        .reduce((acc, cur) => add(acc, cur), zeroDinero(currency))
}

export function toEnumKey(value: string): string {
    return value.toLowerCase().replace(/_/g, "-")
}

// Sum of all items (regardless of payment method).
export function calcTotal(items: TakeawayCartItem[]) {
    return sumItems(items, () => true)
}

// Sum of items that will be charged to the wallet (no explicit payment method, or GOPAY_WALLET).
export function calcWalletTotal(items: TakeawayCartItem[]) {
    return sumItems(items, (i) => {
        const method = i.product.paymentDetails?.method
        return !method || method === PaymentMethodType.GOPAY_WALLET
    })
}
